import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Response } from '@angular/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs/Subscription';
import { SelectItem } from 'primeng/primeng';

import { ApiValidationService, ApiValidator } from 'app/shared/api-validation';
import { User, UserId } from '../user.model';
import { UserRepository } from '../user.repository';
import { Company, Department, DepartmentId, Industry, IndustryId, WorkRecordRepository } from '../work-record';
import { ROLE } from '../roles.const';
import { CompanyModalComponent } from '../work-record/company-modal/company-modal.component';
import { NotifyService, ToastType } from 'app/notify';
import { noop } from 'app/shared';
import { UserGroupId } from 'app/group/user-group.model';
import { LANGUAGE } from 'app/language/languages.const';
import { LanguageService } from 'app/language/language.service';
import { SessionId, Session } from 'app/session';
import { SessionRepository } from 'app/session/session.repository';


@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  providers: [
    ApiValidationService
  ]
})
export class UserFormComponent implements OnInit, OnDestroy {

  public selectedRoles: string[] = [];
  public checkBoxRoles: any[];
  public userForm: FormGroup = null;
  public companiesFiltered: Company[] = [];
  public departmentsFiltered: Department[] = [];
  public industriesFiltered: Industry[] = [];
  public department: Department = null;
  public industry: Industry = null;
  public languageOptions: SelectItem[] = [];

  private user: User = null;
  private groupId: UserGroupId = null;
  private sessionId: SessionId = null;
  private languageSubscription: Subscription = null;
  private currentSessionUsers: User[] = null;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private userRepository: UserRepository,
    private workRecordRepository: WorkRecordRepository,
    private notify: NotifyService,
    private activatedRoute: ActivatedRoute,
    private apiValidation: ApiValidationService,
    private translateService: TranslateService,
    private language: LanguageService,
    private location: Location,
    private sessionRepository: SessionRepository
  ) {
    if (activatedRoute.snapshot.data['user']) {
      this.user = activatedRoute.snapshot.data['user'];
    }

    if (activatedRoute.snapshot.queryParams.hasOwnProperty('groupId')) {
      this.groupId = activatedRoute.snapshot.queryParams['groupId'];
    }

    if (activatedRoute.snapshot.queryParams.hasOwnProperty('sessionId')) {
      this.sessionId = activatedRoute.snapshot.queryParams['sessionId'];
    }
  }

  ngOnInit() {
    this.createForm();
    this.prepareRoles();
    this.languageOptions = this.language.getLanguageOptions(true);

    if (null !== this.user) {
      this.userForm.patchValue(this.user);
      this.selectedRoles = this.user.getRoles();
    }

    if (
      null !== this.user &&
      null !== this.user.workRecord
    ) {
      this.userForm.patchValue(this.user.workRecord);
      this.industry = this.user.workRecord.industry;
      this.department = this.user.workRecord.department;
    }

    if (this.activatedRoute.snapshot.queryParams.hasOwnProperty('role')) {
      this.selectedRoles.push(this.activatedRoute.snapshot.queryParams['role']);
    }

    if (this.sessionId !== null) {
      this.loadSessionUsers(this.sessionId);
    }

    this.languageSubscription = this.language.onLanguageChange
      .subscribe(
        () => {
          this.languageOptions = this.language.getLanguageOptions(true);
        }
      );
    }

  ngOnDestroy() {
    if (null !== this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  public submit({ valid, value }): void {
    if (!valid) {
      return;
    }

    if (null !== this.user) {
      this.update(value);

      return;
    }

    if (null === this.groupId) {
      this.create(value);

      return;
    }

    this.createWithGroupAssign(value);
  }

  public filterCompanies(query: string): void {
    this.workRecordRepository.companyAutocomplete(query)
      .subscribe(
        (companies: Company[]): void => {
          const newCompany: Company = new Company(null, query, null);

          this.companiesFiltered = companies;
          this.companiesFiltered.unshift(newCompany);
        },
        noop
      );
  }

  public selectCompany(company: Company): void {
    if (null === company.getId()) {
      this.notify.modal<CompanyModalComponent, Company>(
        CompanyModalComponent,
        { company }
      )
      .subscribe(
        (newCompany: Company): void => {
          this.userForm.patchValue({ company: newCompany });
        },
        noop
      );
    }
  }

  public onEditButtonClick(): void {
    this.notify.modal<CompanyModalComponent, Company>(
      CompanyModalComponent,
      { company: this.userForm.value.company }
    )
    .subscribe(
      (newCompany: Company): void => {
        this.userForm.patchValue({ company: newCompany });
      },
      noop
    );
  }

  public isButtonDisabled(): boolean {
    return !(this.userForm.value.company instanceof Company);
  }

  public filterDepartments(query: string): void {
    this.workRecordRepository.departmentAutocomplete(query)
      .subscribe(
        (departments: Department[]): void => {
          const newDepartment: Department = new Department(null, query);

          this.departmentsFiltered = departments;
          this.departmentsFiltered.unshift(newDepartment);
        },
        noop
      );
  }

  public submitDepartment({ item }): void {
    if (null === item.id) {
      this.createDepartment(item.name);

      return;
    }

    this.updateDepartment(item);
  }

  public selectDepartment({ item }): void {
    this.userForm.patchValue({ department: new Department(item.id, item.name) });
  }

  public isAutocompleteCompanyValid(): boolean {
    return !(
      this.userForm.value.company instanceof Company &&
      this.userForm.value.company.id !== null
    );
  }

  private prepareRoles(): void {
    this.checkBoxRoles = [
      {
        key: this.translateService.instant('role.' + ROLE.PARTICIPANT),
        value: ROLE.PARTICIPANT
      },
      {
        key: this.translateService.instant('role.' + ROLE.COACH),
        value: ROLE.COACH
      }
    ];
  }

  private updateDepartment(item: any) {
    this.workRecordRepository.departmentUpdate(item.id, item.name)
      .subscribe(
        () => {
          this.userForm.patchValue({ department: new Department(item.id, item.name) });
          this.notify.toast(ToastType.SUCCESS, this.translateService.instant('update.success'));
        },
        () => {
          this.notify.toast(ToastType.ERROR, this.translateService.instant('update.error'));
        }
      );
  }

  private createDepartment(name: string) {
    this.workRecordRepository.departmentCreate(name)
      .subscribe(
        (response: { id: DepartmentId }): void => {
          this.department = new Department(response.id, name);
          this.userForm.patchValue({ department: this.department });
          this.notify.toast(ToastType.SUCCESS, this.translateService.instant('create.success'));
        },
        (): void => {
          this.notify.toast(ToastType.ERROR, this.translateService.instant('create.error'));
        }
      );
  }

  public submitIndustry({ item }): void {
    if (null === item.id) {
      this.createIndustry(item.name);

      return;
    }

    this.updateIndustry(item);
  }

  public selectIndustry({ item }): void {
    this.userForm.patchValue({ industry: new Industry(item.id, item.name) });
  }

  private updateIndustry(item: any) {
    this.workRecordRepository.industryUpdate(item.id, item.name)
      .subscribe(
        () => {
          this.userForm.patchValue({ industry: new Industry(item.id, item.name) });
          this.notify.toast(ToastType.SUCCESS, this.translateService.instant('update.success'));
        },
        () => {
          this.notify.toast(ToastType.ERROR, this.translateService.instant('update.error'));
        }
      );
  }

  private createIndustry(name: string) {
    this.workRecordRepository.industryCreate(name)
      .subscribe(
        (response: { id: IndustryId }): void => {
          this.industry = new Industry(response.id, name);
          this.userForm.patchValue({ industry: this.industry });
          this.notify.toast(ToastType.SUCCESS, this.translateService.instant('create.success'));
        },
        (): void => {
          this.notify.toast(ToastType.ERROR, this.translateService.instant('create.error'));
        }
      );
  }

  public filterIndustries(query: string): void {
    this.workRecordRepository.industryAutocomplete(query)
    .subscribe(
      (industries: Industry[]): void => {
        const newIndystry: Industry = new Industry(null, query);

        this.industriesFiltered = industries;
        this.industriesFiltered.unshift(newIndystry);
      },
      noop
    );
  }

  private create(userData: any): void {
    this.userRepository.create(
      this.selectedRoles,
      userData.name,
      userData.surname,
      userData.phone,
      userData.email,
      userData.language,
      userData.company.getId(),
      userData.department.getId(),
      userData.industry.getId()
    )
    .subscribe(
      (response): void => {
        if (this.sessionId !== null) {
          return this.addUserToSession(response.id)
        }
        this.clearForm();
        this.location.back();
        this.notify.toast(ToastType.SUCCESS, this.translateService.instant('user.message.user_created'));
      },
      (response: Response): void => {
        this.apiValidation.setResponse(response);
        this.notify.toast(ToastType.ERROR, this.translateService.instant('user.error.user_not_created'));
      }
    );
  }

  private createWithGroupAssign(userData: any): void {
    this.userRepository.createWithGroupAssign(
      this.selectedRoles,
      userData.name,
      userData.surname,
      userData.phone,
      userData.email,
      userData.language,
      userData.company.getId(),
      userData.department.getId(),
      userData.industry.getId(),
      this.groupId
    )
    .subscribe(
      (): void => {
        this.clearForm();
        this.router.navigate(['group', this.groupId, 'edit']);
        this.notify.toast(ToastType.SUCCESS, this.translateService.instant('user.message.user_created'));
      },
      (response: Response): void => {
        this.apiValidation.setResponse(response);
        this.notify.toast(ToastType.ERROR, this.translateService.instant('user.error.user_not_created'));
      }
    );
  }

  private update(userData: any): void {
    this.userRepository.update(
        this.user.getId(),
        this.selectedRoles,
        userData.name,
        userData.surname,
        userData.phone,
        userData.language,
        userData.email,
        userData.company.getId(),
        userData.department.getId(),
        userData.industry.getId()
      )
      .subscribe(
        (): void => {
          this.clearForm();
          this.location.back();
          this.notify.toast(ToastType.SUCCESS, this.translateService.instant('user.message.user_updated'));
        },
        (): void => {
          this.notify.toast(ToastType.ERROR, this.translateService.instant('user.error.user_not_updated'));
        }
      );
  }

  private clearForm(): void {
    this.userForm.reset();
  }

  private createForm(): void {
    this.userForm = this.formBuilder.group({
      name: ['', Validators.required],
      surname: ['', Validators.required],
      phone: ['', Validators.required],
      email: ['', [Validators.email, ApiValidator(this.apiValidation, 'email')]],
      language: ['', Validators.required],
      company: ['', Validators.required],
      department: ['', Validators.required],
      industry: ['', Validators.required],
      ROLE_COACH: [],
      ROLE_PARTICIPANT: []
    });
  }

  private loadSessionUsers(sessionId: SessionId): void {
    this.sessionRepository.get(sessionId).subscribe(
      (session: Session) => {
        this.currentSessionUsers = session.participants;
      },
      noop
    );
  }

  private addUserToSession(userId: UserId): void {
    const updatedSessionUsersList = this.currentSessionUsers
      .map(user => user.getId())
      .concat(userId);

    this.sessionRepository.participants(this.sessionId, updatedSessionUsersList)
    .subscribe(
      () => {
        this.clearForm();
        this.location.back();
        this.notify.toast(ToastType.SUCCESS, this.translateService.instant('user.message.user_created'));
      },
      () => {
        this.notify.toast(ToastType.ERROR, this.translateService.instant('session.form.message.error.participant_not_saved'))
      }
    );
  }

}



// WEBPACK FOOTER //
// /dist/src/app/user/user-form/user-form.component.ts